import { useEffect, useRef, useState } from 'react';
import { Check, Copy, Download, Mail, MessageCircle, Share2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Toast } from '@/components/ui/toast';
import { STRINGS } from '@/strings/strings';
import { cn } from '@/lib/utils';
import { buildBookShareUrl } from './book-share-url';
import { isMobileDevice } from './is-mobile-device';

/**
 * Book-arrived share sheet, patterned on the audio ShareBottomSheet.
 *
 * Two selectable option cards (same look as audio variant cards):
 *   - Share the cover (hidden if no cover available OR if the pre-fetch
 *     failed — e.g. file 404 / CORS error)
 *   - Own message
 * Once an option is picked, the same textarea + channel bar renders
 * for both. Textarea is OPTIONAL for cover, REQUIRED for custom.
 *
 * Mobile vs desktop detection (Sebastian ruling 2026-10-22):
 *   `navigator.canShare({ files })` returns `true` on desktop
 *   Chrome/macOS even though the desktop Web-Share UX is effectively
 *   broken. The mobile branch is taken ONLY when BOTH:
 *     1. `canShare({ files: [cover.file] })` returns true, AND
 *     2. the device is touch-primary — `userAgentData.mobile === true`
 *        (modern Chromium) OR `(pointer: coarse) + (hover: none)`
 *        matchMedia combo (Safari / Firefox fallback).
 *   Desktop browsers ALWAYS get the desktop path: visible "Cover
 *   herunterladen" button, channel buttons = text + link exactly like
 *   text mode.
 *
 * Cover handling (Sebastian rulings 2026-10-21 + 2026-10-22):
 *   - The sheet pre-fetches the cover file on open. Chrome
 *     user-activation is one-action-per-gesture; combining the
 *     cover-fetch + download + window.open in a single click
 *     silently dropped the download on real-browser desktop. Moving
 *     the fetch out of the click path makes the download click
 *     synchronous and reliable.
 *   - If the pre-fetch fails (cover file missing / 404 / CORS), the
 *     cover option is hidden entirely — not shown with an error row.
 *   - Desktop (!isMobile OR !canShareFiles): a dedicated "Cover
 *     herunterladen" button inside the cover option is the ONLY
 *     place that downloads. Clicking it fires `<a download>`
 *     synchronously (gesture is live) and shows a toast. The sheet
 *     does NOT close on the download click, so the toast stays
 *     visible. Channel buttons on desktop behave exactly like the
 *     custom option — text + link, no implicit download.
 *   - Mobile (isMobile AND canShareFiles): channel buttons call
 *     `navigator.share({ files, text })` so the OS share sheet picks
 *     the destination (WhatsApp, Instagram Stories, Messages, …).
 *     - AbortError (user cancelled): keep sheet open, do nothing.
 *     - Any other error (NotAllowedError, SecurityError, …): fall
 *       back to the text-mode behaviour for that channel AND reveal
 *       the download button so the user still has a path to the file.
 *       Never close the sheet silently on failure.
 */

type Option = 'cover' | 'custom';

export interface CoverFile {
  file: File;
  /** Original presigned URL — opaque to the sheet, passed to
   *  navigator.share so the OS may show a preview. */
  url: string;
}

interface BookShareSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Resolver for the cover file. Called ONCE on sheet open (not on
   *  click). Must return null on load failure; the sheet then hides
   *  the cover option entirely. */
  fetchCover: () => Promise<CoverFile | null>;
  /** When false the cover option is hidden before pre-fetch is even
   *  attempted. Set by the route after probing
   *  `envelope.cover.imageIsPrintCover`. */
  coverAvailable: boolean;
}

const TOAST_DURATION_MS = 2000;
const COVER_DOWNLOAD_FILENAME = 'dreamable-buch-cover.png';

export function BookShareSheet({
  open,
  onOpenChange,
  fetchCover,
  coverAvailable,
}: BookShareSheetProps) {
  const copy = STRINGS.bookReview.share;
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [customText, setCustomText] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMsg, setToastMsg] = useState<string>('');
  const [coverState, setCoverState] = useState<
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; cover: CoverFile; fileShareSupported: boolean }
    | { kind: 'failed' }
  >({ kind: 'idle' });
  // Mobile Web-Share failed with something OTHER than AbortError
  // (NotAllowedError, SecurityError, …). Reveals the "Cover
  // herunterladen" button so the user still has a path to the file,
  // even on an otherwise-mobile device where the share API refused.
  const [fileShareFellBack, setFileShareFellBack] = useState(false);
  // Desktop-only — set true after the cover download fires. Drives the
  // "attach the file manually" hint next to the toast/button so the
  // customer knows the channel buttons will send only the text + link.
  const [coverDownloaded, setCoverDownloaded] = useState(false);
  // One-shot flag for the pre-fetch effect. Lives in a ref (not state)
  // so the effect body contains no direct setState call — that would
  // trip `react-hooks/set-state-in-effect`. The flag resets on close so
  // the next open re-fetches.
  const prefetchStartedRef = useRef(false);

  // State reset on close via the render-time store-prev-prop pattern
  // (same shape as audio ShareBottomSheet) — the react-hooks/
  // set-state-in-effect rule only flags setState inside useEffect, so
  // the pattern is deliberately render-time.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) {
      setSelectedOption(null);
      setCustomText('');
      setToastVisible(false);
      setCoverState({ kind: 'idle' });
      setFileShareFellBack(false);
      setCoverDownloaded(false);
    }
  }
  // Ref reset on close in a separate effect — refs can only be mutated
  // outside render (per react-hooks/refs). Pure ref mutation; no
  // setState here, so the set-state-in-effect rule stays satisfied.
  useEffect(() => {
    if (!open) prefetchStartedRef.current = false;
  }, [open]);

  // Pre-fetch the cover on open. One-shot per open via `prefetchStartedRef`
  // so React StrictMode's double-fire does not kick off two fetches
  // (and so no cleanup is needed — the previous version cancelled its
  // own only in-flight fetch via the cleanup's `cancelled = true`).
  useEffect(() => {
    if (!open || !coverAvailable) return;
    if (prefetchStartedRef.current) return;
    prefetchStartedRef.current = true;
    void (async () => {
      setCoverState({ kind: 'loading' });
      const resolved = await fetchCover().catch(() => null);
      if (resolved === null) {
        setCoverState({ kind: 'failed' });
        return;
      }
      // Mobile-only gate — see isMobileDevice JSDoc for why the
      // canShare({files}) signal alone is insufficient.
      const fileShareSupported =
        isMobileDevice() &&
        typeof navigator !== 'undefined' &&
        typeof navigator.canShare === 'function' &&
        typeof navigator.share === 'function' &&
        navigator.canShare({ files: [resolved.file] });
      setCoverState({ kind: 'ready', cover: resolved, fileShareSupported });
    })();
  }, [open, coverAvailable, fetchCover]);

  const coverVisible = coverAvailable && coverState.kind === 'ready';
  const coverFile = coverState.kind === 'ready' ? coverState.cover : null;
  const fileShareSupported = coverState.kind === 'ready' ? coverState.fileShareSupported : false;

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setToastVisible(true);
    window.setTimeout(() => setToastVisible(false), TOAST_DURATION_MS);
  };

  const hasText = customText.trim().length > 0;
  const channelsDisabled = selectedOption === 'custom' && !hasText;

  // ONE shared payload builder for both options (Sebastian ruling
  // 2026-10-22). Cover mode has a default accompanying text so a never-
  // typed textarea still produces `text + \n\n + url`, matching the
  // custom-mode behaviour. Pre-fill of the textarea on cover-select
  // keeps the user visually aware of what will be sent; the
  // substitution here is the defense-in-depth for the case where the
  // user explicitly cleared the textarea.
  const resolvedText = (): string => {
    const typed = customText.trim();
    if (typed.length > 0) return typed;
    if (selectedOption === 'cover') return copy.coverMessage;
    return '';
  };

  const payloadText = (medium: 'whatsapp' | 'email' | 'copy' | 'web_share'): string => {
    const content = selectedOption === 'cover' ? 'cover' : 'custom';
    const url = buildBookShareUrl(medium, content);
    const text = resolvedText();
    return text.length > 0 ? `${text}\n\n${url}` : url;
  };

  // On first switch to the cover option, prefill the textarea with the
  // default accompanying text so the user sees what will be sent. Does
  // NOT overwrite anything the user already typed.
  const selectCover = () => {
    if (selectedOption !== 'cover' && customText.length === 0) {
      setCustomText(copy.coverMessage);
    }
    setSelectedOption('cover');
  };
  const selectCustom = () => {
    // If the user is switching away from cover AND the textarea still
    // holds the default cover message (never edited), clear it so the
    // custom placeholder is visible and the user starts fresh.
    if (selectedOption === 'cover' && customText === copy.coverMessage) {
      setCustomText('');
    }
    setSelectedOption('custom');
  };

  const handleDownloadCover = () => {
    if (coverFile === null) return;
    downloadFile(coverFile.file, COVER_DOWNLOAD_FILENAME);
    setCoverDownloaded(true);
    showToast(copy.coverDownloadDone);
    // Sheet does NOT close here — the toast + the "attach the file
    // manually" hint stay visible and the user can then pick a channel.
  };

  // Channel actions. Each returns true iff a user-facing side effect
  // actually happened (new tab opened, clipboard write succeeded,
  // native share accepted). Sheet closes ONLY on success — never
  // silently on failure or user cancellation. Follows Sebastian ruling
  // 2026-10-22 §3: "Do not close the sheet before the share/open call
  // has actually happened."
  const openWhatsApp = (): boolean => {
    const w = window.open(
      'https://wa.me/?text=' + encodeURIComponent(payloadText('whatsapp')),
      '_blank',
      'noopener,noreferrer',
    );
    return w !== null;
  };

  const openEmail = (): boolean => {
    const href =
      'mailto:?subject=' +
      encodeURIComponent(copy.customEmailSubject) +
      '&body=' +
      encodeURIComponent(payloadText('email'));
    const w = window.open(href, '_blank', 'noopener,noreferrer');
    return w !== null;
  };

  const copyText = async (): Promise<boolean> => {
    try {
      await copyToClipboard(payloadText('copy'));
      return true;
    } catch {
      return false;
    }
  };

  const doWebShareNoFile = async (): Promise<{ ok: boolean; aborted: boolean }> => {
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
      const copied = await copyText();
      return { ok: copied, aborted: false };
    }
    try {
      await navigator.share({ text: payloadText('web_share') });
      return { ok: true, aborted: false };
    } catch (err) {
      const aborted = err instanceof Error && err.name === 'AbortError';
      return { ok: false, aborted };
    }
  };

  const runTextChannel = async (channel: 'whatsapp' | 'email' | 'copy' | 'moreApps') => {
    if (channel === 'whatsapp') {
      if (openWhatsApp()) onOpenChange(false);
      return;
    }
    if (channel === 'email') {
      if (openEmail()) onOpenChange(false);
      return;
    }
    if (channel === 'copy') {
      const ok = await copyText();
      if (ok) {
        showToast(STRINGS.storyOutput.page.share.sheet.toast);
        window.setTimeout(() => onOpenChange(false), TOAST_DURATION_MS);
      }
      return;
    }
    // moreApps
    const r = await doWebShareNoFile();
    if (r.ok) onOpenChange(false);
    // aborted or failed → keep sheet open; no silent close.
  };

  const handleChannel = async (channel: 'whatsapp' | 'email' | 'copy' | 'moreApps') => {
    if (channelsDisabled) return;

    // Cover + file-share supported + not previously fallen-back:
    // call navigator.share with the file. On AbortError keep the sheet
    // open and do nothing. On any OTHER error, mark the file-share
    // path as fallen-back (reveals the download button) AND re-run
    // the channel in text-only mode so the user gets SOMETHING.
    if (
      selectedOption === 'cover' &&
      fileShareSupported &&
      !fileShareFellBack &&
      coverFile !== null
    ) {
      const medium =
        channel === 'moreApps'
          ? 'web_share'
          : channel === 'copy'
            ? 'copy'
            : channel === 'email'
              ? 'email'
              : 'whatsapp';
      try {
        await navigator.share({ files: [coverFile.file], text: payloadText(medium) });
        onOpenChange(false);
        return;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // User cancelled — do nothing. Sheet stays open.
          return;
        }
        // Other error (NotAllowedError, SecurityError, …) — reveal
        // the download button and fall through to text-only for this
        // click. The next click of the same channel uses the text
        // path directly because `fileShareFellBack` is now true.
        setFileShareFellBack(true);
      }
    }

    // Everything else (cover + desktop, cover + mobile-fell-back,
    // custom): behave like the text-only pattern — text + link, no
    // implicit download.
    await runTextChannel(channel);
  };

  const subtitle =
    selectedOption === null ? copy.sheetSubtitle : STRINGS.storyOutput.page.share.sheet.sendeUeber;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader className="gap-1 px-6 pt-6">
            <SheetTitle className="text-base font-semibold text-dream-navy">
              {copy.sheetTitle}
            </SheetTitle>
            <SheetDescription className="text-sm text-dream-navy/60">{subtitle}</SheetDescription>
          </SheetHeader>

          <div className="flex flex-col gap-2 px-6 pt-2">
            {coverVisible && (
              <OptionCard
                label={copy.optionCoverLabel}
                subtitle={copy.optionCoverSubtitle}
                emoji="🖼️"
                selected={selectedOption === 'cover'}
                hasSelection={selectedOption !== null}
                onSelect={selectCover}
                testId="book-share-option-cover"
              />
            )}
            <OptionCard
              label={copy.optionTextLabel}
              subtitle={copy.optionTextSubtitle}
              emoji="✍️"
              selected={selectedOption === 'custom'}
              hasSelection={selectedOption !== null}
              onSelect={selectCustom}
              testId="book-share-option-text"
            />
          </div>

          {selectedOption !== null && (
            <div className="mt-4 border-t border-dream-navy/10 px-6 pt-4 pb-6">
              {selectedOption === 'cover' &&
                (!fileShareSupported || fileShareFellBack) &&
                coverFile !== null && (
                  <div className="mb-4 flex flex-col gap-2">
                    <button
                      type="button"
                      data-testid="book-share-cover-download"
                      onClick={handleDownloadCover}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-dream-navy bg-dream-navy px-4 py-3 text-sm font-medium text-dream-white hover:bg-dream-navy/90"
                    >
                      <Download className="h-4 w-4" aria-hidden="true" />
                      <span>{copy.coverDownloadButton}</span>
                    </button>
                    {coverDownloaded && (
                      <p
                        data-testid="book-share-cover-attach-hint"
                        className="text-xs leading-snug text-dream-navy/70"
                      >
                        {copy.coverDesktopAttachHint}
                      </p>
                    )}
                  </div>
                )}

              <label
                className="block text-xs font-medium text-dream-navy/70"
                htmlFor="book-share-custom-text"
              >
                {copy.customLabel}
              </label>
              <textarea
                id="book-share-custom-text"
                data-testid="book-share-custom-text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder={copy.customPlaceholder}
                className="mt-2 w-full resize-none rounded-xl border border-dream-navy/10 bg-dream-background/50 p-3 text-sm text-dream-navy focus:border-dream-navy focus:outline-none"
                rows={4}
              />
              <div className="mt-3 grid grid-cols-4 gap-2">
                <ChannelButton
                  icon={<MessageCircle className="h-5 w-5" strokeWidth={2} />}
                  label={STRINGS.storyOutput.page.share.sheet.channels.whatsapp}
                  onClick={() => void handleChannel('whatsapp')}
                  disabled={channelsDisabled}
                  testId="book-share-channel-whatsapp"
                />
                <ChannelButton
                  icon={<Mail className="h-5 w-5" strokeWidth={2} />}
                  label={STRINGS.storyOutput.page.share.sheet.channels.email}
                  onClick={() => void handleChannel('email')}
                  disabled={channelsDisabled}
                  testId="book-share-channel-email"
                />
                <ChannelButton
                  icon={<Copy className="h-5 w-5" strokeWidth={2} />}
                  label={STRINGS.storyOutput.page.share.sheet.channels.copy}
                  onClick={() => void handleChannel('copy')}
                  disabled={channelsDisabled}
                  testId="book-share-channel-copy"
                />
                <ChannelButton
                  icon={<Share2 className="h-5 w-5" strokeWidth={2} />}
                  label={STRINGS.storyOutput.page.share.sheet.channels.moreApps}
                  onClick={() => void handleChannel('moreApps')}
                  disabled={channelsDisabled}
                  testId="book-share-channel-more"
                  variant="primary"
                />
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Toast message={toastMsg} visible={toastVisible} />
    </>
  );
}

interface OptionCardProps {
  label: string;
  subtitle: string;
  emoji: string;
  selected: boolean;
  hasSelection: boolean;
  onSelect: () => void;
  testId: string;
}

function OptionCard({
  label,
  subtitle,
  emoji,
  selected,
  hasSelection,
  onSelect,
  testId,
}: OptionCardProps) {
  const ariaSelected = STRINGS.storyOutput.page.share.sheet.ariaLabel.variantSelected;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-start gap-3 rounded-xl border bg-dream-background/50 p-3 text-left transition-all',
        selected
          ? 'border-dream-navy bg-dream-navy/5'
          : 'border-dream-navy/10 hover:border-dream-navy/30',
        hasSelection && !selected && 'opacity-50',
      )}
    >
      <span className="text-xl leading-none" aria-hidden="true">
        {emoji}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium text-dream-navy">{label}</span>
          {selected && (
            <Check
              className="h-4 w-4 text-dream-navy"
              strokeWidth={2.5}
              aria-label={ariaSelected}
            />
          )}
        </span>
        <span className="mt-1 block text-xs leading-snug text-dream-navy/60">{subtitle}</span>
      </span>
    </button>
  );
}

interface ChannelButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
  variant?: 'default' | 'primary';
}

function ChannelButton({
  icon,
  label,
  onClick,
  disabled,
  testId,
  variant = 'default',
}: ChannelButtonProps) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex flex-col items-center justify-center gap-1.5 rounded-xl border px-2 py-3 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        variant === 'primary'
          ? 'border-dream-navy bg-dream-navy text-dream-white hover:bg-dream-navy/90'
          : 'border-dream-navy/10 bg-dream-background/50 text-dream-navy hover:border-dream-navy/30',
      )}
    >
      {icon}
      <span className="text-center leading-tight">{label}</span>
    </button>
  );
}

async function copyToClipboard(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      /* fall through */
    }
  }
  if (typeof document === 'undefined') return;
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.top = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(ta);
  }
}

function downloadFile(file: File, filename: string): void {
  if (typeof document === 'undefined') return;
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
